import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Col, Divider, Flex, Row } from 'antd';

import TotalValue from '@totem/components/software/vulnerabilitySummary/TotalValue';
import VulnerabilitySummaryContext from '@totem/components/vulnerabilities/overviewSummary/vulnerabilitySummaryContext';
import { getProbabilityStyle } from '@totem/components/vulnerabilities/report/utilities';
import colors from '@totem/styles/colors';
import { isNotNull } from '@totem/utilities/common';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const styles = {
  borderLeft: {
    borderLeftColor: colors.neutral.silver,
    borderLeftStyle: 'solid' as 'solid',
    borderLeftWidth: 1,
  },
  selectable: {
    cursor: 'pointer',
  },
  valueSmaller: {
    fontSize: '18px',
    paddingBottom: '5px',
  },
  valueEmphasis: {
    fontSize: '28px',
    fontWeight: 700,
  },
  icon: {
    fontSize: '36px',
  },
  confirmed: {
    color: colors.event.criticality.background.critical,
  },
  likely: {
    color: colors.event.criticality.background.major,
  },
  possible: {
    color: colors.event.criticality.background.warning,
  },
  default: {
    color: colors.event.criticality.background.unknown,
  },
};

const DeviceSummaryLayout = () => {
  const { data } = useContext(VulnerabilitySummaryContext);

  return (
    <div>
      {isNotNull(data) && (
        <>
          <Divider titlePlacement={'start'}>Device</Divider>
          <Row>
            <Col span={6}>
              <TotalValue
                title={
                  <>
                    <Link to={`/dashboard/deviceVulnerabilities`}>
                      {isNotNull(data) && isNotNull(data.uniqueOwners)
                        ? data.uniqueOwners.count
                        : 0}
                    </Link>{' '}
                    Devices
                  </>
                }
                value={
                  <>
                    <br />
                    {isNotNull(data) &&
                      isNotNull(data.probability) &&
                      data.probability
                        .sort((compA, compB) =>
                          sortStringAscending(compA.name, compB.name),
                        )
                        .map((item) => (
                          <Flex
                            key={item.name}
                            justify={'space-between'}
                            gap={'large'}
                            style={styles.valueSmaller}
                          >
                            <div style={getProbabilityStyle(item.name)}>
                              {item.name}
                            </div>
                            <div>
                              <span
                                style={{
                                  ...styles.valueEmphasis,
                                  ...getProbabilityStyle(item.name),
                                }}
                              >
                                <Link
                                  to={`/dashboard/deviceVulnerabilities?probability=${item.name}`}
                                >
                                  {item.count.toLocaleString()}
                                </Link>
                              </span>{' '}
                              <span
                                title={'Common Vulnerabilities and Exposures'}
                              >
                                CVE
                              </span>
                              s
                            </div>
                          </Flex>
                        ))}
                  </>
                }
              />
            </Col>
            <Col span={6} style={styles.borderLeft}>
              <TotalValue
                title={
                  <>
                    <Link to={`/dashboard/deviceVulnerabilities?b=t`}>
                      {isNotNull(data) && isNotNull(data.uniqueBuildings)
                        ? data.uniqueBuildings.count
                        : 0}
                    </Link>{' '}
                    Buildings
                  </>
                }
                value={
                  <>
                    <br />
                    {isNotNull(data) &&
                      isNotNull(data.buildingSummary) &&
                      data.buildingSummary
                        .sort((compA, compB) =>
                          sortStringAscending(compA.name, compB.name),
                        )
                        .map((item) => (
                          <Flex
                            key={item.name}
                            justify={'space-between'}
                            gap={'large'}
                            style={styles.valueSmaller}
                          >
                            <div style={getProbabilityStyle(item.name)}>
                              {item.name}
                            </div>
                            <div>
                              <span
                                style={{
                                  ...styles.valueEmphasis,
                                  ...getProbabilityStyle(item.name),
                                }}
                              >
                                <Link
                                  to={`/dashboard/deviceVulnerabilities?probability=${item.name}&b=t`}
                                >
                                  {item.vulnerabilityCount.toLocaleString()}
                                </Link>
                              </span>{' '}
                              <span
                                title={'Common Vulnerabilities and Exposures'}
                              >
                                CVE
                              </span>
                              s
                            </div>
                          </Flex>
                        ))}
                  </>
                }
              />
            </Col>
            <Col span={6} style={styles.borderLeft}>
              <TotalValue
                title={
                  <>
                    <Link to={`/dashboard/deviceVulnerabilities?cs=t`}>
                      {isNotNull(data) && isNotNull(data.uniqueControlSystems)
                        ? data.uniqueControlSystems.count
                        : 0}
                    </Link>{' '}
                    Control Systems
                  </>
                }
                value={
                  <>
                    <br />
                    {isNotNull(data) &&
                      isNotNull(data.controlSystemSummary) &&
                      data.controlSystemSummary
                        .sort((compA, compB) =>
                          sortStringAscending(compA.name, compB.name),
                        )
                        .map((item) => (
                          <Flex
                            key={item.name}
                            justify={'space-between'}
                            gap={'large'}
                            style={styles.valueSmaller}
                          >
                            <div style={getProbabilityStyle(item.name)}>
                              {item.name}
                            </div>
                            <div>
                              <span
                                style={{
                                  ...styles.valueEmphasis,
                                  ...getProbabilityStyle(item.name),
                                }}
                              >
                                <Link
                                  to={`/dashboard/deviceVulnerabilities?probability=${item.name}&cs=t`}
                                >
                                  {item.vulnerabilityCount.toLocaleString()}
                                </Link>
                              </span>{' '}
                              <span
                                title={'Common Vulnerabilities and Exposures'}
                              >
                                CVE
                              </span>
                              s
                            </div>
                          </Flex>
                        ))}
                  </>
                }
              />
            </Col>
            <Col span={6} style={styles.borderLeft}>
              <TotalValue
                title={`Unique CVEs`}
                value={
                  <>
                    <span style={styles.valueSmaller}>
                      <br />
                      <span style={styles.valueEmphasis}>
                        {isNotNull(data) &&
                          isNotNull(data.unique) &&
                          data.unique.count.toLocaleString()}
                      </span>{' '}
                      <span title={'Common Vulnerabilities and Exposures'}>
                        CVE
                      </span>
                      s
                    </span>
                  </>
                }
              />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default DeviceSummaryLayout;
